import { useTranslation } from 'react-i18next';
import { useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import styles from './AdminReportsPage.module.css';

interface ReportCategory {
  titleKey: string;
  categoryKey: string;
}

const REPORT_CATEGORIES: ReportCategory[] = [
  { titleKey: 'Institution Profile', categoryKey: 'admin.reports.authoritative' },
  { titleKey: 'Campus Setup', categoryKey: 'admin.reports.operational' },
  { titleKey: 'Active Users', categoryKey: 'admin.reports.authoritative' },
  { titleKey: 'Role Assignments', categoryKey: 'admin.reports.authoritative' },
  { titleKey: 'Enrolment Summaries', categoryKey: 'admin.reports.operational' },
  { titleKey: 'Academic Year Configuration', categoryKey: 'admin.reports.operational' },
  { titleKey: 'Product Adoption', categoryKey: 'admin.reports.authoritative' },
  { titleKey: 'Integration Health', categoryKey: 'admin.reports.operational' },
  { titleKey: 'Configuration Changes', categoryKey: 'admin.reports.operational' },
];

function formatTime(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return ts;
  }
}

export function AdminReportsPage() {
  const { t } = useTranslation();

  const { state, reload } = useAsyncData(
    'admin-reports',
    () => api.admin.reports.list(),
    true,
  );

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.reports.title')}</h1>
        <p className="page-subtitle">{t('admin.reports.subtitle')}</p>
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('admin.reports.noReports')}
      >
        {(apiReports) => (
          <div className={styles.reportGrid}>
            {REPORT_CATEGORIES.map((cat, idx) => {
              const apiMatch = apiReports.find((r) => r.title === cat.titleKey || r.category === cat.titleKey);
              return (
                <Card key={idx} className={styles.reportCard}>
                  <h3 className={styles.reportTitle}>{cat.titleKey}</h3>
                  <div className={styles.reportMeta}>
                    <span className={styles.categoryBadge}>{t(cat.categoryKey)}</span>
                    <span className={styles.sourceBadge}>
                      {apiMatch?.source ?? 'CampusOne Core'}
                    </span>
                    <span className={styles.freshnessBadge}>
                      <span className={styles.freshnessDot} />
                      {t('admin.reports.dataFreshness')}: {t('admin.reports.operational')}
                    </span>
                  </div>
                  <div className={styles.reportDetails}>
                    <span>
                      <span className={styles.detailLabel}>{t('admin.reports.sourceSystem')}:</span>{' '}
                      {apiMatch?.source ?? 'Core'}
                    </span>
                    <span>
                      <span className={styles.detailLabel}>{t('admin.reports.institutionScope')}:</span>{' '}
                      {t('admin.dashboard.title')}
                    </span>
                    <span>
                      <span className={styles.detailLabel}>{t('admin.reports.generated')}:</span>{' '}
                      {apiMatch?.id ? formatTime(new Date().toISOString()) : '—'}
                    </span>
                  </div>
                  <div className={styles.reportActions}>
                    <button type="button" className={styles.generateBtn} onClick={() => {}}>
                      {t('admin.reports.generate')}
                    </button>
                    <button type="button" className={styles.downloadBtn} disabled={!apiMatch?.id}>
                      {t('admin.reports.download')}
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </AsyncPanel>
    </div>
  );
}
